import i18n from '@/i18n';
import { nextTick } from 'vue';

const Trans = {
  get defaultLocale() {
    return 'pl';
  },

  get supportedLocales() {
    return ['pl', 'en', 'es', 'ua', 'uk', 'de'];
  },

  get currentLocale() {
    return i18n.global.locale.value;
  },

  set currentLocale(newLocale) {
    i18n.global.locale.value = newLocale;
  },

  async switchLanguage(newLocale) {
    await Trans.loadLocaleMessages(newLocale);
    Trans.currentLocale = newLocale;
    document.querySelector('html').setAttribute('lang', newLocale);
    localStorage.setItem('language', newLocale);
  },

  async loadLocaleMessages(locale) {
    if (!i18n.global.availableLocales.includes(locale)) {
      const messages = await import(`./locales/${locale}.json`);
      i18n.global.setLocaleMessage(locale, messages.default);
    }

    return nextTick();
  },

  isLocaleSupported(locale) {
    return Trans.supportedLocales.includes(locale);
  },

  getUserLocale() {
    const locale =
      window.navigator.language ||
      window.navigator.userLanguage ||
      Trans.defaultLocale;

    return {
      locale: locale,
      localeNoRegion: locale.split('-')[0],
    };
  },

  getPersistedLocale() {
    const persistedLocale = localStorage.getItem('language');

    if (Trans.isLocaleSupported(persistedLocale)) {
      return persistedLocale;
    } else {
      return null;
    }
  },

  guessDefaultLocale() {
    const userPersistedLocale = Trans.getPersistedLocale();
    if (userPersistedLocale) {
      return userPersistedLocale;
    }

    const userPreferredLocale = Trans.getUserLocale();

    if (Trans.isLocaleSupported(userPreferredLocale.locale)) {
      return userPreferredLocale.locale;
    }

    if (Trans.isLocaleSupported(userPreferredLocale.localeNoRegion)) {
      return userPreferredLocale.localeNoRegion;
    }

    return Trans.defaultLocale;
  },

  async routeMiddleware(to, _from, next) {
    let paramLocale = to.params.locale;
    if (paramLocale === 'uk') {
      // TODO Workaround for routing uk -> ua
      paramLocale = 'ua';
    }

    if (!paramLocale) {
      return next('/' + Trans.guessDefaultLocale() + to.path);
    }

    if (!Trans.isLocaleSupported(paramLocale)) {
      return next(Trans.guessDefaultLocale());
    }

    if (Trans.currentLocale != paramLocale) {
      await Trans.switchLanguage(paramLocale);
    }

    return next();
  },

  i18nRoute(to) {
    return {
      ...to,
      params: {
        locale: Trans.currentLocale,
        ...to.params,
      },
    };
  },

  t(...params) {
    return i18n.global.t(...params);
  },

  tm(...params) {
    return i18n.global.tm(...params);
  },
};

export default Trans;
